import { useMemo, useCallback, createContext, ReactNode, useState, useContext, Dispatch, SetStateAction } from 'react';
import { FieldValues, useFormContext } from 'react-hook-form';

import { useDefaultErrorMessage } from './wizard-application-wizard.utils';
import { FieldOptionsType, useWorkerFieldOptions } from './worker-application-wizard.hooks';

const WorkerApplicationContext = createContext<IWorkerApplicationContextType | null>(null);

export const WorkerApplicationCxtProvider = ({ children }: { children: ReactNode }) => {
  const { overlayModalContent, setOverlayModalContent, toggleApplicationOverlayModal } = useOverlayModal();
  const {
    formState: { errors },
  } = useFormContext();
  const defaultErrorMessage = useDefaultErrorMessage();
  const [workerId, setWorkerId] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasAppliedJob, setHasAppliedJob] = useState(false);
  // The first-step fields the backend answered a 409 on ("email", "phone_number", or both).
  // Which input carries the conflict decides which one offers the login link, so the message
  // lands on the field the applicant actually has to change.
  const [conflictFields, setConflictFields] = useState<string[]>([]);
  const [selectedDisplacementAvailabilityValue, setDisplacementAvailability] = useState('');
  const [skippedSteps, setSkippedSteps] = useState<number[]>([]);
  const [fourthStepDecision, setFourthStepDecision] = useState<string | null>(null);
  const [userModifying, toggleUserModify] = useState(false);
  const [isFirstStepSuccessful, setIsFirstStepSuccessful] = useState(false);
  const [userConfirmation, getUserConfirmation] = useState(false);
  const errorsTextMap: FieldValues = Object.keys(errors).reduce((acc, curr, i) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const errorObj: any = errors[curr];
    let message = { [curr]: errorObj?.message || defaultErrorMessage };
    if (errorObj[i] && typeof errorObj[i] === 'object') {
      message = Object.keys(errorObj[i]).reduce(
        (a, c) => ({ ...a, [`${curr}.${i}.${c}`]: errorObj[c]?.message || defaultErrorMessage }),
        {}
      );
    }
    return {
      ...acc,
      ...message,
    };
  }, {});

  const fieldOptions = useWorkerFieldOptions();
  const value = useMemo(
    () => ({
      overlayModalContent,
      setOverlayModalContent,
      toggleApplicationOverlayModal,
      errorsTextMap,
      setWorkerId,
      workerId,
      setLoading,
      loading,
      setSkippedSteps,
      skippedSteps,
      setConflictFields,
      conflictFields,
      selectedDisplacementAvailabilityValue,
      setDisplacementAvailability,
      fieldOptions,
      fourthStepDecision,
      setFourthStepDecision,
      setHasAppliedJob,
      hasAppliedJob,
      toggleUserModify,
      userModifying,
      isFirstStepSuccessful,
      setIsFirstStepSuccessful,
      userConfirmation,
      getUserConfirmation,
    }),
    [
      overlayModalContent,
      setOverlayModalContent,
      toggleApplicationOverlayModal,
      errorsTextMap,
      workerId,
      loading,
      skippedSteps,
      conflictFields,
      selectedDisplacementAvailabilityValue,
      fieldOptions,
      fourthStepDecision,
      hasAppliedJob,
      userModifying,
      isFirstStepSuccessful,
      userConfirmation,
    ]
  );
  return <WorkerApplicationContext.Provider value={value}>{children}</WorkerApplicationContext.Provider>;
};

const useOverlayModal = () => {
  const [overlayModalContent, setOverlayModalContent] = useState<OverlayModalType>({
    visible: false,
  });
  const toggleApplicationOverlayModal = useCallback(
    (visible: boolean) =>
      setOverlayModalContent({
        ...overlayModalContent,
        visible,
      }),
    [overlayModalContent]
  );

  return {
    overlayModalContent,
    setOverlayModalContent,
    toggleApplicationOverlayModal,
  };
};
export const useWorkerApplicationCxt: useWorkerApplicationCxtType = () => {
  const currentCxtValues = useContext(WorkerApplicationContext);
  if (!currentCxtValues)
    throw new Error('useWorkerApplicationCxt has to be used within <WorkerApplicationContext.Provider>');
  return currentCxtValues;
};
interface IWorkerApplicationContextType {
  overlayModalContent: OverlayModalType;
  setOverlayModalContent: Dispatch<SetStateAction<OverlayModalType>>;
  toggleApplicationOverlayModal: (x: boolean) => void;
  errorsTextMap: FieldValues;
  setWorkerId: Dispatch<SetStateAction<string>>;
  workerId: string;
  setLoading: Dispatch<SetStateAction<boolean>>;
  loading: boolean;
  setHasAppliedJob: Dispatch<SetStateAction<boolean>>;
  hasAppliedJob: boolean;
  setSkippedSteps: Dispatch<SetStateAction<number[]>>;
  skippedSteps: number[];
  setConflictFields: Dispatch<SetStateAction<string[]>>;
  conflictFields: string[];
  setDisplacementAvailability: Dispatch<SetStateAction<string>>;
  selectedDisplacementAvailabilityValue: string;
  setFourthStepDecision: Dispatch<SetStateAction<string | null>>;
  fourthStepDecision: string | null;
  fieldOptions: FieldOptionsType;
  toggleUserModify: Dispatch<SetStateAction<boolean>>;
  userModifying: boolean;
  isFirstStepSuccessful: boolean;
  setIsFirstStepSuccessful: Dispatch<SetStateAction<boolean>>;
  userConfirmation: boolean;
  getUserConfirmation: Dispatch<SetStateAction<boolean>>;
}
type OverlayModalType = {
  content?: ReactNode;
  visible: boolean;
  key?: string;
};
type useWorkerApplicationCxtType = () => IWorkerApplicationContextType;
